import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useCompanySettings, useSiteSettings, useModuleSettings, resolveUploadUrl } from "@/hooks/useCompanySettings";
import { notificationsApi } from "@/services/api";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import SessionTimeoutModal from "@/components/feature/SessionTimeoutModal";
import { getStoredPermissions } from "@/hooks/usePagePermission";
import ServerStatusBadge from "@/components/feature/ServerStatusBadge";

// ── Brand amber palette ──────────────────────────────────────────────────────
const BRAND = {
  sidebar:     "#1c1008",   // very dark amber-brown sidebar
  sidebarHov:  "rgba(251,191,36,0.08)",
  active:      "rgba(251,191,36,0.18)",
  activeText:  "#fbbf24",   // amber-400
  accent:      "#f59e0b",   // amber-500
  accentDark:  "#d97706",   // amber-600
  adminBg:     "rgba(251,191,36,0.12)",
  adminText:   "#fcd34d",   // amber-300
  adminActive: "rgba(251,191,36,0.22)",
};

interface NavItem {
  label: string;
  icon: string;
  path: string;
  badge?: number;
  children?: { label: string; path: string }[];
  adminOnly?: boolean;
  roles?: string[];   // restrict to specific roles
  external?: boolean; // open in new window
}

interface ERPLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

const NAV_MODULE_MAP: Record<string, string> = {
  "/clients":      "Clients",
  "/job-cards":    "Job Cards",
  "/billing":      "Billing",
  "/employees":    "Employees",
  "/reports":      "Reports",
  "/settings":     "Settings",
  "/deliverables": "Deliverables",
  "/media":        "Media Library",
};

// Admin sub-items with role restrictions
const ADMIN_NAV: NavItem[] = [
  { label: "Admin Overview",       icon: "ri-dashboard-line",          path: "/admin",                        roles: ["super_admin","admin","superadmin"] },
  { label: "User Management",      icon: "ri-user-settings-line",      path: "/admin/users",                  roles: ["super_admin","admin","superadmin"] },
  { label: "Roles & Permissions",  icon: "ri-shield-keyhole-line",     path: "/admin/roles",                  roles: ["super_admin","superadmin"] },
  { label: "Site Visit Requests",  icon: "ri-calendar-event-line",     path: "/admin/site-visits",            roles: ["super_admin","admin","superadmin","survey_manager"] },
  { label: "Media Library",        icon: "ri-image-2-line",            path: "/media",                        roles: ["super_admin","admin","superadmin","survey_manager","office_staff"] },
  { label: "Notifications Config", icon: "ri-notification-badge-line", path: "/admin/notifications-config",   roles: ["super_admin","admin","superadmin"] },
  { label: "Audit Logs",           icon: "ri-file-search-line",        path: "/admin/audit-logs",             roles: ["super_admin","admin","superadmin"] },
  { label: "Settings",             icon: "ri-settings-5-line",         path: "/admin/settings",               roles: ["super_admin","admin","superadmin"] },
  { label: "Database & Backup",    icon: "ri-database-2-line",         path: "/admin/database",               roles: ["super_admin","superadmin"] },
];

// Quick admin actions for header dropdown
const QUICK_ADMIN_ACTIONS = [
  { label: "Add New User",         icon: "ri-user-add-line",           path: "/admin/users",                  color: "#f59e0b" },
  { label: "Manage Roles",         icon: "ri-shield-keyhole-line",     path: "/admin/roles",                  color: "#d97706" },
  { label: "View Audit Logs",      icon: "ri-file-search-line",        path: "/admin/audit-logs",             color: "#b45309" },
  { label: "Settings",             icon: "ri-settings-5-line",         path: "/admin/settings",               color: "#f59e0b" },
  { label: "Database & Backup",    icon: "ri-database-2-line",         path: "/admin/database",               color: "#d97706" },
  { label: "Site Visit Requests",  icon: "ri-calendar-event-line",     path: "/admin/site-visits",            color: "#b45309" },
];

export default function ERPLayout({ children, title, subtitle }: ERPLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings: companySettings } = useCompanySettings();
  const siteSettings = useSiteSettings();
  const { modules } = useModuleSettings();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(["/job-cards", "/billing"]);
  const [user, setUser] = useState<{ name: string; role: string; avatar: string; avatarUrl?: string } | null>(null);

  // Helper to check if a string is a valid, complete URL (not truncated)
  const isValidUrl = (str: string | undefined): boolean => {
    if (!str || str.length < 10) return false;
    // Must start with http:// or https://
    if (!str.startsWith('http://') && !str.startsWith('https://')) {
      // Allow relative URLs starting with /
      return str.startsWith('/') && str.length > 1;
    }
    // Check for truncated URLs like https://jo/ or https://jobcard/
    // A valid URL should have a proper domain (at least one dot or be localhost)
    try {
      const url = new URL(str);
      // Reject URLs that look truncated (domain too short or no TLD)
      const hostname = url.hostname;
      if (hostname.length < 4) return false; // Too short to be a real domain
      if (hostname.includes('.') && hostname.split('.').some(part => part.length === 0)) return false;
      // Allow localhost
      if (hostname === 'localhost') return true;
      return true;
    } catch {
      return false;
    }
  };
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [adminActionsOpen, setAdminActionsOpen] = useState(false);
  const [liveNotifs, setLiveNotifs] = useState<Array<{ id: number; title: string; message: string; time: string; is_read?: boolean; read?: boolean }>>([]);

  const loadUser = () => {
    const stored = localStorage.getItem("erp_user");
    if (!stored) { navigate("/login"); return; }
    const parsedUser = JSON.parse(stored);
    // Ensure avatarUrl is set from the avatar field (only if it's a valid URL)
    if (parsedUser.avatar && !parsedUser.avatarUrl && isValidUrl(parsedUser.avatar)) {
      parsedUser.avatarUrl = parsedUser.avatar;
    }
    // Clear invalid avatarUrl to prevent broken image errors
    if (parsedUser.avatarUrl && !isValidUrl(parsedUser.avatarUrl)) {
      parsedUser.avatarUrl = undefined;
    }
    setUser(parsedUser);
  };

  useEffect(() => {
    loadUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Listen for avatar/profile updates from settings page
  useEffect(() => {
    const handleUserUpdated = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        const updated = { ...detail };
        // Validate avatar URL before setting
        if (updated.avatar && !updated.avatarUrl && isValidUrl(updated.avatar)) {
          updated.avatarUrl = updated.avatar;
        }
        // Clear invalid avatarUrl
        if (updated.avatarUrl && !isValidUrl(updated.avatarUrl)) {
          updated.avatarUrl = undefined;
        }
        setUser(updated);
      } else {
        loadUser();
      }
    };
    window.addEventListener("erp-user-updated", handleUserUpdated);
    return () => window.removeEventListener("erp-user-updated", handleUserUpdated);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchNotifs = () => {
      notificationsApi.getAll()
        .then((data) => {
          const list = data?.data || data || [];
          setLiveNotifs(Array.isArray(list) ? list : []);
        })
        .catch(() => setLiveNotifs([]));
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { setMobileSidebarOpen(false); }, [location.pathname]);

  const [, setPermVersion] = useState(0);
  useEffect(() => {
    const handler = () => setPermVersion((v) => v + 1);
    window.addEventListener("erp-permissions-updated", handler);
    return () => window.removeEventListener("erp-permissions-updated", handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("erp_user");
    localStorage.removeItem("token");
    navigate("/login");
  };

  const toggleMenu = (path: string) => {
    setExpandedMenus((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (item: NavItem) => {
    if (isActive(item.path)) return true;
    if (item.children) return item.children.some((c) => isActive(c.path));
    return location.pathname.startsWith(item.path) && item.path !== "/";
  };

  const { showWarning, secondsLeft, keepAlive, forceLogout } = useSessionTimeout({
    timeoutMs: 30 * 60 * 1000,
    warningMs: 2 * 60 * 1000,
  });

  const unreadNotifs = liveNotifs.filter((n) => !n.is_read && !n.read);
  const recentNotifs = liveNotifs.slice(0, 5);

  const adminRoles = ["super_admin", "admin", "superadmin"];
  const isAdmin = user ? adminRoles.includes(user.role) : false;
  const isSuperAdmin = user ? ["super_admin", "superadmin"].includes(user.role) : false;

  const billingChildren = [
    ...(modules.module_invoices   ? [{ label: "Invoices",   path: "/billing" }] : []),
    ...(modules.module_quotations ? [{ label: "Quotations", path: "/billing/quotations" }] : []),
    ...(modules.module_payments   ? [{ label: "Payments",   path: "/billing/payments" }] : []),
  ];

  // ── Main nav ─────────────────────────────────────────────────────────────
  const mainNavItems: NavItem[] = [
    { label: "Dashboard",  icon: "ri-dashboard-3-line",     path: "/dashboard" },
    { label: "Clients",    icon: "ri-contacts-book-2-line", path: "/clients" },
    {
      label: "Job Cards",
      icon: "ri-file-list-3-line",
      path: "/job-cards",
      children: [
        { label: "All Job Cards", path: "/job-cards" },
        { label: "Create New",    path: "/job-cards/new" },
      ],
    },
    ...(billingChildren.length > 0 ? [{
      label: "Billing & Payments",
      icon: "ri-bill-line",
      path: "/billing",
      children: billingChildren,
    }] : []),
    {
      label: "Employees",
      icon: "ri-team-line",
      path: "/employees",
      children: [
        { label: "All Employees",     path: "/employees" },
        { label: "Leave Calendar",    path: "/employees/leave-calendar" },
        { label: "Team Availability", path: "/employees/availability" },
      ],
    },
    ...(modules.module_field_tracking ? [{ label: "Field Tracking", icon: "ri-map-pin-user-line", path: "/field-tracking" }] : []),
    ...(modules.module_deliverables   ? [{ label: "Deliverables",   icon: "ri-folder-3-line",     path: "/deliverables" }] : []),
    ...(modules.module_client_portal  ? [{ label: "Client Portal",  icon: "ri-global-line",        path: "/client-portal" }] : []),
    ...(modules.module_reports        ? [{ label: "Reports",        icon: "ri-bar-chart-2-line",   path: "/reports" }] : []),
  ];

  const bottomNavItems: NavItem[] = [
    { label: "Field App", icon: "ri-smartphone-line", path: "/field-app", external: true },
  ];

  // Filter admin nav by role
  const visibleAdminNav = useMemo(() => {
    if (!user) return [];
    return ADMIN_NAV.filter((item) => {
      if (!item.roles) return isAdmin;
      return item.roles.includes(user.role);
    });
  }, [user, isAdmin]);

  // Filter quick admin actions by role
  const visibleQuickActions = useMemo(() => {
    if (!user || !isAdmin) return [];
    return QUICK_ADMIN_ACTIONS.filter((a) => {
      if (a.path === "/admin/roles" || a.path === "/admin/settings" || a.path === "/admin/database") {
        return isSuperAdmin;
      }
      return true;
    });
  }, [user, isAdmin, isSuperAdmin]);

  const visibleMainItems = useMemo(() => {
    if (!user) return mainNavItems;
    const savedPerms = getStoredPermissions();
    const roleKey = Object.keys(savedPerms).find(
      (k) => k.trim().toLowerCase() === (user.role || "").trim().toLowerCase()
    );
    if (!roleKey) return mainNavItems;
    const rolePerms = savedPerms[roleKey];
    return mainNavItems.filter((item) => {
      const moduleName = NAV_MODULE_MAP[item.path];
      if (!moduleName) return true;
      const perms = rolePerms[moduleName] ?? [];
      return perms.includes("view");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, modules]);

  const softwareName = siteSettings.site_title || companySettings.name || "SurveyPro ERP";
  const softwareLogo = resolveUploadUrl(siteSettings.site_logo_url || companySettings.logo);

  const mobileBottomNav = [
    { label: "Dashboard", icon: "ri-dashboard-3-line", path: "/dashboard" },
    { label: "Job Cards",  icon: "ri-file-list-3-line", path: "/job-cards" },
    { label: "Billing",    icon: "ri-bill-line",         path: "/billing" },
    { label: "Employees",  icon: "ri-team-line",         path: "/employees" },
  ];

  const isAdminPage = location.pathname.startsWith("/admin") || location.pathname === "/media";

  // ── Sidebar nav item renderer ─────────────────────────────────────────────
  const renderNavItem = (item: NavItem, collapsed: boolean) => {
    const isExternal = item.external;
    const content = (
      <>
        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i className={`${item.icon} text-lg`} />
        </span>
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{item.label}</span>
            {item.badge !== undefined && (
              <span className="text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold" style={{ background: BRAND.accent }}>
                {item.badge}
              </span>
            )}
            {isExternal && (
              <span className="w-4 h-4 flex items-center justify-center opacity-60">
                <i className="ri-external-link-line text-xs" />
              </span>
            )}
            {item.children && !isExternal && (
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-base transition-transform ${expandedMenus.includes(item.path) ? "rotate-180" : ""}`} />
              </span>
            )}
          </>
        )}
      </>
    );

    const baseStyles = "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap";
    const activeStyles = isParentActive(item)
      ? { background: BRAND.active, color: BRAND.activeText }
      : { color: "rgba(255,255,255,0.6)" };
    const hoverHandlers = {
      onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
        if (!isParentActive(item)) (e.currentTarget as HTMLElement).style.background = BRAND.sidebarHov;
        if (!isParentActive(item)) (e.currentTarget as HTMLElement).style.color = "#fff";
      },
      onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
        if (!isParentActive(item)) (e.currentTarget as HTMLElement).style.background = "transparent";
        if (!isParentActive(item)) (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.6)";
      },
    };

    return (
      <div key={item.path}>
        {isExternal ? (
          <a
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            className={baseStyles}
            style={activeStyles}
            {...hoverHandlers}
          >
            {content}
          </a>
        ) : (
          <button
            onClick={() => {
              if (item.children) {
                toggleMenu(item.path);
                if (collapsed) setSidebarOpen(true);
              } else {
                navigate(item.path);
              }
            }}
            className={baseStyles}
            style={activeStyles}
            {...hoverHandlers}
          >
            {content}
          </button>
        )}
      {item.children && !collapsed && expandedMenus.includes(item.path) && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l pl-3" style={{ borderColor: "rgba(251,191,36,0.2)" }}>
          {item.children.map((child) => (
            <Link
              key={child.path}
              to={child.path}
              className="block px-3 py-2 rounded-lg text-xs font-medium transition-all"
              style={
                isActive(child.path)
                  ? { color: BRAND.activeText, background: BRAND.active }
                  : { color: "rgba(255,255,255,0.5)" }
              }
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
  };

  const renderAdminNavItem = (item: NavItem, collapsed: boolean) => (
    <button
      key={item.path}
      onClick={() => navigate(item.path)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
      style={
        isParentActive(item)
          ? { background: BRAND.adminActive, color: BRAND.adminText }
          : { color: "rgba(255,255,255,0.45)" }
      }
      onMouseEnter={(e) => {
        if (!isParentActive(item)) (e.currentTarget as HTMLElement).style.background = BRAND.sidebarHov;
        if (!isParentActive(item)) (e.currentTarget as HTMLElement).style.color = BRAND.adminText;
      }}
      onMouseLeave={(e) => {
        if (!isParentActive(item)) (e.currentTarget as HTMLElement).style.background = "transparent";
        if (!isParentActive(item)) (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.45)";
      }}
    >
      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
        <i className={`${item.icon} text-lg`} style={isParentActive(item) ? { color: BRAND.adminText } : {}} />
      </span>
      {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
    </button>
  );

  // ── Sidebar content ───────────────────────────────────────────────────────
  const SidebarContent = ({ collapsed }: { collapsed: boolean }) => (
    <>
      {/* Brand */}
      <div className="flex items-center gap-3 px-4 py-5 flex-shrink-0" style={{ borderBottom: "1px solid rgba(251,191,36,0.12)" }}>
        <div className="w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center" style={{ background: "rgba(251,191,36,0.15)" }}>
          {softwareLogo ? (
            <img src={softwareLogo} alt="Logo" className="w-6 h-6 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          ) : (
            <i className="ri-survey-line text-lg" style={{ color: BRAND.activeText }} />
          )}
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <p className="text-white font-bold text-sm leading-tight truncate">{softwareName}</p>
            <p className="text-xs truncate" style={{ color: "rgba(251,191,36,0.5)" }}>{siteSettings.site_tagline || "Enterprise ERP"}</p>
          </div>
        )}
      </div>

      {/* Scrollable nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 scrollbar-thin">
        {/* Main */}
        {!collapsed && (
          <p className="text-xs font-semibold uppercase tracking-wider px-3 pb-1.5 pt-1" style={{ color: "rgba(251,191,36,0.35)" }}>Main</p>
        )}
        {visibleMainItems.map((item) => renderNavItem(item, collapsed))}

        {/* General */}
        <div className="pt-2">
          {!collapsed && (
            <p className="text-xs font-semibold uppercase tracking-wider px-3 pb-1.5 pt-1" style={{ color: "rgba(251,191,36,0.35)" }}>General</p>
          )}
          {bottomNavItems.map((item) => renderNavItem(item, collapsed))}
        </div>

        {/* Admin Control */}
        {visibleAdminNav.length > 0 && (
          <div className="pt-2 mt-1" style={{ borderTop: "1px solid rgba(251,191,36,0.12)" }}>
            {!collapsed && (
              <div className="flex items-center gap-2 px-3 pb-1.5 pt-2">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-shield-star-line text-xs" style={{ color: BRAND.adminText }} />
                </span>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "rgba(252,211,77,0.5)" }}>
                  Admin Control
                </p>
              </div>
            )}
            {visibleAdminNav.map((item) => renderAdminNavItem(item, collapsed))}
          </div>
        )}
      </nav>

      {/* User profile */}
      {user && (
        <div className="p-3 flex-shrink-0" style={{ borderTop: "1px solid rgba(251,191,36,0.12)" }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden" style={{ background: BRAND.accentDark }}>
              {user.avatarUrl && isValidUrl(user.avatarUrl)
                ? <img src={user.avatarUrl} alt="avatar" className="w-full h-full object-cover" />
                : user.avatar}
            </div>
            {!collapsed && (
              <div className="flex-1 overflow-hidden">
                <p className="text-white text-xs font-semibold truncate">{user.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {isAdmin && (
                    <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded font-medium leading-none" style={{ background: "rgba(251,191,36,0.15)", color: BRAND.adminText }}>
                      <i className="ri-shield-star-line text-xs" />Admin
                    </span>
                  )}
                  <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.4)" }}>{user.role.replace(/_/g, " ")}</p>
                </div>
              </div>
            )}
            {!collapsed && (
              <button onClick={handleLogout}
                className="w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer"
                style={{ color: "rgba(255,255,255,0.4)" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "#ef4444"; (e.currentTarget as HTMLElement).style.background = "rgba(239,68,68,0.1)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.4)"; (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                title="Logout"
              >
                <i className="ri-logout-box-r-line text-base" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      {!collapsed && (
        <div className="px-4 py-3 flex-shrink-0" style={{ borderTop: "1px solid rgba(251,191,36,0.06)" }}>
          <p className="text-xs text-center" style={{ color: "rgba(255,255,255,0.18)" }}>
            &copy; {new Date().getFullYear()} {softwareName}
          </p>
          <p className="text-xs text-center mt-0.5" style={{ color: "rgba(255,255,255,0.12)" }}>
            Powered by{" "}
            <a href="https://digicube.net.in" target="_blank" rel="noopener noreferrer"
              className="underline underline-offset-2 transition-colors cursor-pointer"
              style={{ color: "rgba(251,191,36,0.5)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = BRAND.activeText; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "rgba(251,191,36,0.5)"; }}
            >
              Digicube Solutions
            </a>
          </p>
        </div>
      )}
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "#fafaf8" }}>

      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col flex-shrink-0 transition-all duration-300 h-screen ${sidebarOpen ? "w-64" : "w-16"}`}
        style={{ background: BRAND.sidebar }}
      >
        <SidebarContent collapsed={!sidebarOpen} />
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 flex flex-col z-50 lg:hidden transition-transform duration-300 ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ background: BRAND.sidebar }}
      >
        <button onClick={() => setMobileSidebarOpen(false)}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer z-10"
          style={{ color: "rgba(255,255,255,0.5)" }}
        >
          <i className="ri-close-line text-lg" />
        </button>
        <SidebarContent collapsed={false} />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Top header */}
        <header className="flex-shrink-0 bg-white border-b border-amber-100/60 px-4 md:px-6 py-0 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex w-8 h-8 items-center justify-center rounded-lg text-gray-500 hover:bg-amber-50 hover:text-amber-700 transition-all cursor-pointer"
            >
              <i className="ri-menu-line text-lg" />
            </button>
            <button onClick={() => setMobileSidebarOpen(true)}
              className="flex lg:hidden w-8 h-8 items-center justify-center rounded-lg text-gray-500 hover:bg-amber-50 transition-all cursor-pointer"
            >
              <i className="ri-menu-line text-lg" />
            </button>

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Link to="/dashboard" className="hover:text-amber-600 transition-colors hidden sm:inline">Home</Link>
              {isAdminPage && (
                <>
                  <i className="ri-arrow-right-s-line text-gray-300 hidden sm:inline" />
                  <span className="font-medium text-sm hidden sm:inline" style={{ color: BRAND.accentDark }}>Admin</span>
                </>
              )}
              {title && (
                <>
                  <i className="ri-arrow-right-s-line text-gray-300 hidden sm:inline" />
                  <span className="text-gray-800 font-medium text-sm truncate max-w-[140px] md:max-w-none">{title}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-2">

            {/* Admin Mode badge */}
            {isAdminPage && isAdmin && (
              <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap" style={{ background: "#fffbeb", color: BRAND.accentDark }}>
                <i className="ri-shield-star-line text-xs" />
                Admin Mode
              </div>
            )}

            {/* Server status — admin pages */}
            {isAdminPage && (
              <div className="hidden sm:block">
                <ServerStatusBadge variant="pill" refreshInterval={30000} />
              </div>
            )}

            {/* Search */}
            <div className="relative hidden md:block">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm" />
              </span>
              <input
                type="text"
                placeholder="Search jobs, clients..."
                className="pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg w-44 lg:w-52 focus:outline-none focus:ring-2 focus:border-amber-400 transition-all"
                style={{ "--tw-ring-color": "rgba(245,158,11,0.25)" } as React.CSSProperties}
              />
            </div>

            {/* Quick Admin Actions dropdown */}
            {isAdmin && (
              <div className="relative">
                <button
                  onClick={() => { setAdminActionsOpen(!adminActionsOpen); setNotifOpen(false); setUserMenuOpen(false); setProfileMenuOpen(false); }}
                  className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition-all whitespace-nowrap"
                  style={adminActionsOpen
                    ? { background: "#fffbeb", borderColor: BRAND.accent, color: BRAND.accentDark }
                    : { borderColor: "#fde68a", color: BRAND.accentDark, background: "#fffbeb" }
                  }
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-star-line text-sm" /></span>
                  Quick Admin
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${adminActionsOpen ? "rotate-180" : ""}`} />
                  </span>
                </button>
                {adminActionsOpen && (
                  <div className="absolute right-0 top-10 w-56 bg-white rounded-xl border border-amber-100 z-50 overflow-hidden py-1"
                    style={{ boxShadow: "0 8px 24px rgba(245,158,11,0.15)" }}
                  >
                    <div className="px-3 py-2 border-b border-amber-50">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Admin Actions</p>
                    </div>
                    {visibleQuickActions.map((a) => (
                      <button key={a.path}
                        onClick={() => { setAdminActionsOpen(false); navigate(a.path); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-amber-50 cursor-pointer transition-all"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: "#fffbeb" }}>
                          <i className={`${a.icon} text-sm`} style={{ color: a.color }} />
                        </span>
                        {a.label}
                      </button>
                    ))}
                    <div className="border-t border-amber-50 mt-1 pt-1">
                      <button onClick={() => { setAdminActionsOpen(false); navigate("/admin"); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold cursor-pointer transition-all"
                        style={{ color: BRAND.accentDark }}
                      >
                        <i className="ri-arrow-right-s-line" />
                        Go to Admin Overview
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => { setNotifOpen(!notifOpen); setUserMenuOpen(false); setAdminActionsOpen(false); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-amber-50 hover:text-amber-700 transition-all cursor-pointer relative"
              >
                <i className="ri-notification-3-line text-lg" />
                {unreadNotifs.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold">
                    {unreadNotifs.length}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-10 w-72 md:w-80 bg-white rounded-xl border border-amber-100 z-50 overflow-hidden"
                  style={{ boxShadow: "0 8px 24px rgba(245,158,11,0.12)" }}
                >
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="font-semibold text-sm text-gray-800">Notifications</span>
                    <Link to="/notifications" onClick={() => setNotifOpen(false)} className="text-xs font-medium cursor-pointer hover:underline" style={{ color: BRAND.accentDark }}>
                      View all
                    </Link>
                  </div>
                  {recentNotifs.map((n) => {
                    const isUnread = !n.is_read && !n.read;
                    return (
                      <Link key={n.id} to="/notifications" onClick={() => setNotifOpen(false)}
                        className={`flex items-start gap-3 px-4 py-3 hover:bg-amber-50/50 cursor-pointer border-b border-gray-50 last:border-0 ${isUnread ? "bg-amber-50/40" : ""}`}
                      >
                        {isUnread && <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: BRAND.accent }} />}
                        <div className={isUnread ? "" : "pl-3.5"}>
                          <p className="text-xs font-semibold text-gray-800">{n.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(n.time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                  <div className="px-4 py-2.5 border-t border-gray-100">
                    <Link to="/notifications" onClick={() => setNotifOpen(false)}
                      className="block text-center text-xs font-medium hover:underline cursor-pointer"
                      style={{ color: BRAND.accentDark }}
                    >
                      See all {liveNotifs.length} notifications
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Profile button */}
            <div className="relative hidden md:block">
              <button
                onClick={() => { setProfileMenuOpen(!profileMenuOpen); setNotifOpen(false); setUserMenuOpen(false); setAdminActionsOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-all cursor-pointer whitespace-nowrap text-sm font-medium"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-settings-line text-sm" /></span>
                Profile
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${profileMenuOpen ? "rotate-180" : ""}`} />
                </span>
              </button>
              {profileMenuOpen && (
                <div className="absolute right-0 top-10 w-52 bg-white rounded-xl border border-amber-100 z-50 overflow-hidden"
                  style={{ boxShadow: "0 8px 24px rgba(245,158,11,0.12)" }}
                >
                  <button onClick={() => { setProfileMenuOpen(false); navigate("/settings?tab=profile"); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 cursor-pointer transition-all"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-3-line text-sm" /></span>My Profile
                  </button>
                  <button onClick={() => { setProfileMenuOpen(false); navigate("/settings?tab=password"); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 cursor-pointer transition-all"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-lock-password-line text-sm" /></span>Change Password
                  </button>
                  <div className="border-t border-gray-100" />
                  <button onClick={() => { setProfileMenuOpen(false); navigate("/settings?tab=notifications"); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 cursor-pointer transition-all"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-notification-3-line text-sm" /></span>Notifications
                  </button>
                </div>
              )}
            </div>

            {/* User menu */}
            {user && (
              <div className="relative">
                <button
                  onClick={() => { setUserMenuOpen(!userMenuOpen); setNotifOpen(false); setAdminActionsOpen(false); }}
                  className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-amber-50 transition-all cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold overflow-hidden" style={{ background: BRAND.accentDark }}>
                    {user.avatarUrl && isValidUrl(user.avatarUrl)
                      ? <img src={user.avatarUrl} alt="avatar" className="w-full h-full object-cover" />
                      : user.avatar}
                  </div>
                  <span className="text-sm font-medium text-gray-700 hidden md:block">{user.name}</span>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-gray-400 text-sm" />
                  </span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-10 w-52 bg-white rounded-xl border border-amber-100 z-50 overflow-hidden"
                    style={{ boxShadow: "0 8px 24px rgba(245,158,11,0.12)" }}
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-800">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.role.replace(/_/g, " ")}</p>
                    </div>
                    <button onClick={() => { setUserMenuOpen(false); navigate("/settings?tab=profile"); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-amber-50 cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-sm" /></span>Edit Profile
                    </button>
                    <button onClick={() => { setUserMenuOpen(false); navigate("/settings?tab=password"); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-amber-50 cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-lock-password-line text-sm" /></span>Change Password
                    </button>
                    <button onClick={() => { setUserMenuOpen(false); navigate("/settings"); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-amber-50 cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-settings-3-line text-sm" /></span>Settings
                    </button>
                    <div className="border-t border-gray-100" />
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-logout-box-r-line text-sm" /></span>Logout
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main id="erp-main-scroll" className="flex-1 overflow-y-auto">
          <div className="pb-16 lg:pb-0">
            {children}
          </div>
        </main>

        {/* Footer */}
        <footer className="flex-shrink-0 bg-white border-t border-amber-100/60 px-4 md:px-6 py-2 items-center justify-between flex-wrap gap-1 hidden lg:flex">
          <p className="text-xs text-gray-400">
            &copy; {new Date().getFullYear()} {softwareName}. All rights reserved.
          </p>
          <p className="text-xs text-gray-400">
            Powered by{" "}
            <a href="https://digicube.net.in" target="_blank" rel="noopener noreferrer"
              className="hover:underline cursor-pointer font-medium transition-colors"
              style={{ color: BRAND.accentDark }}
            >
              Digicube Solutions
            </a>
          </p>
        </footer>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-amber-100/60 flex items-center justify-around px-1 py-1 lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 4px)" }}
      >
        {mobileBottomNav.map((item) => {
          const active = location.pathname.startsWith(item.path);
          return (
            <button key={item.path} onClick={() => navigate(item.path)}
              className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all cursor-pointer flex-1"
              style={active ? { color: BRAND.accentDark } : { color: "#9ca3af" }}
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className={`${item.icon} text-xl`} />
              </span>
              <span className="text-xs font-medium whitespace-nowrap">{item.label}</span>
              {active && <span className="w-1 h-1 rounded-full" style={{ background: BRAND.accent }} />}
            </button>
          );
        })}
        <button onClick={() => setMobileSidebarOpen(true)}
          className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl text-gray-400 hover:text-gray-600 transition-all cursor-pointer flex-1"
        >
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-menu-2-line text-xl" />
          </span>
          <span className="text-xs font-medium whitespace-nowrap">More</span>
        </button>
      </nav>

      {/* Click-outside overlay */}
      {(notifOpen || userMenuOpen || profileMenuOpen || adminActionsOpen) && (
        <div className="fixed inset-0 z-40" onClick={() => { setNotifOpen(false); setUserMenuOpen(false); setProfileMenuOpen(false); setAdminActionsOpen(false); }} />
      )}

      {showWarning && (
        <SessionTimeoutModal secondsLeft={secondsLeft} onKeepAlive={keepAlive} onLogout={forceLogout} />
      )}
    </div>
  );
}
